import React, { createContext, useContext, useMemo } from "react";

/**
 * The signed-in platform operator, for every Super Admin screen.
 *
 * SuperAdminLayout reads `GET /super-admin/me` once per page load and provides it here:
 *   { is_super_admin: bool, is_operator: bool, role: string, role_label: string,
 *     permissions: string[], email: string, name: string, picture: string }
 * so there is exactly one answer to "may this person do X" per page load, and no screen
 * fetches the permissions again. The server enforces every permission on its own; this is
 * what lets a screen hide a control the server would refuse, instead of offering it.
 *
 * Super-admin-only screens are gated on `isSuperAdmin`, not on a permission: their routes use
 * `require_super_admin`, which ignores the permission list.
 */
const OperatorContext = createContext(null);

export function OperatorProvider({ me, children }) {
  const value = useMemo(() => {
    const granted = new Set(Array.isArray(me?.permissions) ? me.permissions : []);
    return {
      me,
      isSuperAdmin: me?.is_super_admin === true,
      can: (permission) => granted.has(permission),
    };
  }, [me]);
  return <OperatorContext.Provider value={value}>{children}</OperatorContext.Provider>;
}

/** `{ me, isSuperAdmin, can(permission) }`. Only valid under `<OperatorProvider>`. */
export function useOperator() {
  const value = useContext(OperatorContext);
  if (!value) throw new Error("useOperator() must be used under <OperatorProvider> (SuperAdminLayout provides it)");
  return value;
}
